import { CardProps, Divider, Typography, useTheme } from '@material-ui/core';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import SettingsBackupRestoreOutlinedIcon from '@material-ui/icons/SettingsBackupRestoreOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import { useLoggedInUserIsSuperUser } from 'app/auth/useLoggedInUserIsSuperUser';
import { PeppolRecordsQueryParamsLsKey } from 'app/localStorageKeys';
import { calcSalesDetailRoute } from 'app/main/financial/routes';
import { CodeBlock } from 'app/main/test/CodeBlock';
import { RadioIsSelectedButton } from 'framework/components/buttons/RadioIsSelectedButton';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CardX } from 'framework/components/cards/CardX';
import { CollapseX } from 'framework/components/CollapseX';
import { ColorX } from 'framework/components/color/ColorX';
import { useThemeColor } from 'framework/components/color/useThemeColor';
import { CaptionVerticalLabelledPropertyX } from 'framework/components/labelledProperty/CaptionVerticalLabelledPropertyX';
import { LabelledPropertyX } from 'framework/components/labelledProperty/LabelledPropertyX';
import { InnerPageXWithStickySidebar } from 'framework/components/layout/InnerPageXWithStickySidebar';
import { QueryResultPaginationX } from 'framework/components/pagination/QueryResultPaginationX';
import { SearchbarX } from 'framework/components/search/SearchbarX';
import { IQueryContext } from 'framework/context/IQueryContext';
import { QueryProvider as FrameworkQueryProvider } from 'framework/context/QueryProvider';
import { AlertDialog } from 'framework/dialogs/AlertDialog';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { useQueryContext } from 'framework/hooks/useQueryContext';
import { useStateBoolean } from 'framework/hooks/useStateBool';
import { onResetToPage1 } from 'framework/types/onResetToPage1';
import { formatDate } from 'framework/utils/date/formatDate';
import {
	filterSortPagePeppolRecords_concurrencyToken,
	filterSortPagePeppolRecords_handle,
	ignorePeppolRecord_handle,
	IPeppolIntegrationStatus,
	IPeppolRecord,
	IPeppolRecordsQueryParams,
	IQueryResult,
	markPeppolAttemptAsFailed_handle,
	PeppolEnvironment,
	sendInvoiceToPeppol_handle,
	undoIgnorePeppolRecord_handle,
} from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { IReloadTriggerProps } from 'shared/layout/IReloadTriggerProps';
import { PeppolRecordStatusChip, PeppolRecordStatusColorRecord } from './peppol.components';

type PeppolRecordsTabProps = IReloadTriggerProps & {
	status: IPeppolIntegrationStatus;
	reload: VoidFunction;
};

export const PeppolRecordsTab = ({ status, reload, reloadTrigger }: PeppolRecordsTabProps) => {
	const strings = useLocalization();
	const { reload: reloadQuery, queryResult, params, setParams } = useQueryContext(PeppolRecordsQueryContext);
	const [selectedId, setSelectedId] = useState<string>();
	const expandedRecord = useMemo(() => queryResult?.values.find(t => t.id === selectedId), [queryResult, selectedId]);

	useLazyEffect(() => {
		reloadQuery();
	}, [reloadTrigger]);

	if (queryResult === undefined) {
		return <div></div>;
	}

	return (
		<InnerPageXWithStickySidebar
			maxWidth={1280}
			stickySidebar={
				<div
					className='df-col gap-16 mh100'
					style={{ padding: 16, paddingRight: 8, maxWidth: 400 }}>
					<PeppolListFilterCard
						params={params}
						setParams={setParams}
						style={{ minWidth: 220 }}
					/>
					<div className='fg1'></div>
					<Divider />
				</div>
			}>
			<div
				className='df-row gap-16 h100'
				style={{
					padding: 16,
					paddingLeft: 8,
				}}>
				<div className='df-col gap-16'>
					<SearchbarX
						placeholder={`${strings.searchOnReference}...`}
						value={params.searchString}
						onSearch={val => setParams(onResetToPage1({ ...params, searchString: val }))}
					/>
					<div
						className='df-col'
						style={{ gap: 8 }}>
						{queryResult.values.map(t => (
							<PeppolRecordListItemCard
								key={t.id}
								item={t}
								isSelected={selectedId === t.id}
								toggle={() => setSelectedId(selectedId === t.id ? undefined : t.id)}
							/>
						))}
						<QueryResultPaginationX
							queryResult={queryResult}
							setPageParams={pageParams => setParams({ ...params, pageParams: pageParams })}
							style={{ justifyContent: 'end', paddingTop: 12, marginBottom: -4 }}
						/>
					</div>
				</div>
				{expandedRecord !== undefined && expandedRecord.id === selectedId && (
					<>
						<Divider orientation='vertical' />
						<PeppolRecordDetailStack
							item={expandedRecord}
							env={status.environment}
							reload={reload}
							reloadTrigger={reloadTrigger}
						/>
					</>
				)}
			</div>
		</InnerPageXWithStickySidebar>
	);
};

type PeppolListFilterCardProps = CardProps & {
	params: IPeppolRecordsQueryParams;
	setParams: React.Dispatch<React.SetStateAction<IPeppolRecordsQueryParams>>;
};

const PeppolListFilterCard = ({ params, setParams, style, ...rest }: PeppolListFilterCardProps) => {
	const strings = useLocalization();
	const onFilter = (pars: IPeppolRecordsQueryParams) => setParams(onResetToPage1(pars));

	return (
		<CardX
			{...rest}
			style={style}
			className='df-col gap-4'>
			<div className='df-row-ac'>
				<span style={{ fontWeight: '500' }}>{strings.filter}</span>
			</div>
			<RadioIsSelectedButton
				isSelected={params.prefilter === 'All'}
				onClick={() => onFilter({ ...params, prefilter: 'All' })}>
				{strings.all}
			</RadioIsSelectedButton>
			<RadioIsSelectedButton
				isSelected={params.prefilter === 'Ignored'}
				onClick={() => onFilter({ ...params, prefilter: 'Ignored' })}>
				{strings.ignored}
			</RadioIsSelectedButton>
			<RadioIsSelectedButton
				isSelected={params.prefilter === 'Unhandled'}
				onClick={() => onFilter({ ...params, prefilter: 'Unhandled' })}>
				{strings.unhandled}
			</RadioIsSelectedButton>
		</CardX>
	);
};

export const PeppolRecordsQueryContext = React.createContext<IQueryContext<IQueryResult<IPeppolRecord>, IPeppolRecordsQueryParams>>(undefined as any);

const DefaultIPeppolRecordsQueryParams: IPeppolRecordsQueryParams = {
	searchString: '',
	sortParams: { direction: 'desc', property: 'created' },
	pageParams: { index: 1, size: 10 },
	prefilter: 'Unhandled',
};

export const PeppolRecordsQueryProvider = ({ children }: { children: any }) => {
	return (
		<FrameworkQueryProvider<IQueryResult<IPeppolRecord>, IPeppolRecordsQueryParams>
			defaultParams={DefaultIPeppolRecordsQueryParams}
			queryF={filterSortPagePeppolRecords_handle}
			concurrencyF={filterSortPagePeppolRecords_concurrencyToken}
			localStorageKey={PeppolRecordsQueryParamsLsKey}
			context={PeppolRecordsQueryContext}>
			{children}
		</FrameworkQueryProvider>
	);
};

const MajorFontSize = 16;

type PeppolRecordListItemCardProps = {
	item: IPeppolRecord;
	isSelected: boolean;
	toggle: VoidFunction;
	style?: React.CSSProperties;
};

export const PeppolRecordListItemCard = ({ item, isSelected, toggle, style }: PeppolRecordListItemCardProps) => {
	const strings = useLocalization();
	const theme = useTheme();
	const color = useMemo<ColorX>(() => PeppolRecordStatusColorRecord[item.status], [item]);
	const tColor = useThemeColor(color);
	const [hovered, enter, leave] = useStateBoolean(false);

	return (
		<CardX
			raised={isSelected || hovered}
			className='df-row w100'
			onMouseEnter={enter}
			onMouseLeave={leave}
			onClick={toggle}
			style={{
				borderRight: isSelected ? `6px solid ${theme.palette.primary.main}` : 'unset',
				cursor: 'pointer',
				...style,
			}}>
			<div style={{ marginRight: 8, color: tColor, fontSize: 22 }}>
				<PeppolRecordListItemStatusIcon
					item={item}
					style={{ fontSize: 22, color: tColor }}
				/>
			</div>
			<div className='df-col gap-4 fg1'>
				<span style={{ fontSize: MajorFontSize, fontWeight: 500 }}>{item.reference}</span>
				<PeppolRecordStatusChip
					status={item.status}
					color={color}
					style={{ marginRight: 'auto' }}
				/>
				<LabelledPropertyX label={strings.date}>{formatDate(item.created)}</LabelledPropertyX>
			</div>
		</CardX>
	);
};

type PeppolRecordListItemStatusIconProps = {
	item: IPeppolRecord;
	style?: React.CSSProperties;
};

export const PeppolRecordListItemStatusIcon = ({ item, style }: PeppolRecordListItemStatusIconProps) => {
	if (item.status === 'Sent') {
		return <CheckCircleOutlineIcon style={style} />;
	} else if (item.status !== 'Ignored') {
		return <HighlightOffOutlinedIcon style={style} />;
	} else {
		return <BlockIcon style={style} />;
	}
};

type PeppolRecordDetailStackProps = IReloadTriggerProps & {
	env: PeppolEnvironment;
	item: IPeppolRecord;
	reload: VoidFunction;
};

const PeppolRecordDetailStack = ({ env, item, reload }: PeppolRecordDetailStackProps) => {
	const isSuperUser = useLoggedInUserIsSuperUser();
	const strings = useLocalization();
	const color = React.useMemo<ColorX>(() => PeppolRecordStatusColorRecord[item.status], [item]);
	const { open, confirm, cancel } = useDialogsContext(reload);
	const { push } = useHistory();

	const onIgnore = () => {
		open(
			<AlertDialog
				open
				title={strings.ignoreSale}
				content={strings.doYouWantToIgnoreSaleReferenceQuestion(item.reference!)}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => ignorePeppolRecord_handle({ id: item.id })}
				accept={confirm}
			/>
		);
	};

	const onRevertIgnore = () => {
		open(
			<AlertDialog
				open
				title={strings.revertIgnoreSale}
				content={strings.doYouWantToRevertIgnoreSaleReferenceQuestion(item.reference!)}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => undoIgnorePeppolRecord_handle({ id: item.id })}
				accept={confirm}
			/>
		);
	};

	const onMarkAttemptAsFailure = (attemptId: string) => {
		open(
			<AlertDialog
				open
				title={`Mark attempt as failure`}
				content={`Continue?`}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => markPeppolAttemptAsFailed_handle({ peppolRecordId: item.id, attemptId: attemptId })}
				accept={confirm}
			/>
		);
	};

	const onForward = () => {
		open(
			<AlertDialog
				open
				title={strings.forward}
				content={`${strings.doYouWantToForwardInvoiceQuestion}\n${strings.reference}: ${item.reference}\n(on peppol network: ${env})`}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => sendInvoiceToPeppol_handle({ id: item.id })}
				accept={confirm}
			/>
		);
	};

	return (
		<div className='df-col gap-16 fg1'>
			<CardX>
				<div
					className='df-row-ac gap-8'
					style={{ marginBottom: 16 }}>
					<Typography
						component='div'
						style={{ fontSize: '16px', fontWeight: 500 }}>
						{item.reference}
					</Typography>
					<PeppolRecordStatusChip
						status={item.status}
						color={color}
						style={{ marginRight: 'auto' }}
					/>
				</div>
				<div className='df-col gap-8'>
					<CaptionVerticalLabelledPropertyX
						label={strings.reference}
						link={() => push(calcSalesDetailRoute(item.salesId))}>
						<span>{item.reference}</span>
					</CaptionVerticalLabelledPropertyX>
					<CaptionVerticalLabelledPropertyX label={strings.date}>{formatDate(item.created)}</CaptionVerticalLabelledPropertyX>
					<Divider />
					{item.lastAttempt && (
						<>
							<CaptionVerticalLabelledPropertyX label={`Status doorsturen`}>{formatDate(item.lastAttempt.created)}</CaptionVerticalLabelledPropertyX>
							<PeppolRecordStatusChip status={item.lastAttempt.status} />
							{item.lastAttempt.status === 'Invalid' && (
								<>
									<div>{`Validation ERRORS:`}</div>
									{item.lastAttempt.validationErrors.map((t, i) => (
										<div key={i}>{t}</div>
									))}
								</>
							)}
							{isSuperUser && (
								<TextLinkButton
									startIcon={<DeleteOutlineOutlinedIcon />}
									hoverColor='secondary'
									onClick={() => onMarkAttemptAsFailure(item.lastAttempt.id)}>
									{`Mark attempt as failure (only for super-users)`}
								</TextLinkButton>
							)}
							<Divider />
						</>
					)}
					<div className='fg1'></div>
					<div className='df-col gap-8'>
						<TextLinkButton
							startIcon={<VisibilityOutlinedIcon />}
							onClick={() => push(calcSalesDetailRoute(item.salesId))}>
							{strings.viewSale}
						</TextLinkButton>
						<TextLinkButton
							startIcon={<MailOutlineIcon />}
							onClick={onForward}
							disabled={item.status === 'Sent'}>
							{strings.forward}
						</TextLinkButton>
						{item.status !== 'Sent' && item.status !== 'Ignored' && (
							<TextLinkButton
								startIcon={<DeleteOutlineOutlinedIcon />}
								hoverColor='secondary'
								onClick={onIgnore}>
								{strings.ignoreSaleImperative}
							</TextLinkButton>
						)}
						{item.status === 'Ignored' && (
							<TextLinkButton
								startIcon={<SettingsBackupRestoreOutlinedIcon />}
								color='secondary'
								hoverColor='secondary'
								onClick={onRevertIgnore}>
								{strings.revertIgnoreSale}
							</TextLinkButton>
						)}
					</div>
				</div>
			</CardX>
			{isSuperUser && (
				<CardX>
					<Typography>{`Only visible for super-users!`}</Typography>
					<CollapseX collapseAfter>
						<CodeBlock
							value={item}
							style={{ margin: 0 }}
						/>
					</CollapseX>
				</CardX>
			)}
		</div>
	);
};
